import { useEffect, useMemo, useRef, useState } from "react";

import { ChevronDoubleRightIcon, ViewColumnsIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import ContextPanel, { PanelTab } from "../../components/messagesInbox/ContextPanel";
import ConversationList from "../../components/messagesInbox/ConversationList";
import { IInboxNotice, InboxError, InboxLiveContext, InboxLoading } from "../../components/messagesInbox/InboxBits";
import InboxTopBar, { MAIN_FOLDERS, MORE_FOLDERS } from "../../components/messagesInbox/InboxTopBar";
import ThreadView, { STATUS_LABELS } from "../../components/messagesInbox/ThreadView";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { useAuth } from "../../contexts/AuthContext";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useCustomerDetails from "../../data/useCustomerDetails";
import useMessagesInbox, { useMessagesInboxPartContext, useMessagesInboxThread } from "../../data/useMessagesInbox";
import useMessagesInboxMutations from "../../data/useMessagesInboxMutations";
import { useFeatureEnabled } from "../../hooks/useFeatureFlag";
import i18n from "../../i18n";
import { InboxCancelReason } from "../../models/messagesInbox/IInboxApi";
import {
  IFitmentCheck,
  IInboxAttachment,
  IInboxConversation,
  IInboxMessage,
  IInboxUser,
  InboxChannel,
  InboxFolder,
  IOrderAction,
} from "../../models/messagesInbox/IInboxConversation";
import { SAMPLE_ME_ID, sampleInboxTemplates, sampleInboxUsers } from "../../stubs/messagesInboxSamples";
import { detectRegs, displayReg } from "../../utils/detectRegs";
import { arrivedPendingIds, mergeRow, messagesFromThread, withThreadAndContext } from "../../utils/inboxApiMapping";
import { buyerFirstName, fillMergeFields } from "../../utils/fillMergeFields";
import formatDateTime from "../../utils/formatDateTime";
import { buyerFacingDate, INBOX_TIME } from "../../utils/inboxDates";
import { inFolder, InboxSort, matchesChannel, matchesSearch, sortBySent, sortConversations } from "../../utils/inboxFolders";
import { fitmentOverall, partKeyCode } from "../../utils/partKeyCode";
import classNames from "../../utils/classNames";
import PriceFormat from "../../utils/priceFormat";
import isLocalDebug from "../../utils/isLocalDebug";
import { replyByLabel } from "../../utils/replyByLabel";
import NoAccess from "../Error/NoAccess";

const ALL_FOLDERS = [...MAIN_FOLDERS, ...MORE_FOLDERS].map((f) => f.id);

let localId = 0;
const nextId = () => `local-${++localId}`;

function regFromThread(c: IInboxConversation) {
  if (c.order) return null;
  const regs = c.messages.filter((m) => m.direction === "buyer").flatMap((m) => detectRegs(m.body));
  return regs.length > 0 ? regs[regs.length - 1] : null;
}

/** Stands in for the real donor compare, which costs a paid lookup. Clearly labelled as a sample. */
function sampleCompare(c: IInboxConversation, reg: string, now: DateTime): IFitmentCheck {
  const d = c.donor;
  const rows = [
    ["Make", d?.make],
    ["Model", d?.model],
    ["Series", d?.series ?? "—"],
    ["Model years", d?.year ? String(d.year) : "—"],
    ["Engine size", d?.engineSize ?? "—"],
    ["Engine code", d?.engineCode ?? "—"],
    ["Fuel", d?.fuel ?? "—"],
    ["Gearbox", d?.gearbox ?? "—"],
    ...(d?.gearboxCode ? [["Gearbox code", d.gearboxCode]] : []),
  ].map(([field, value]) => ({ field: field as string, buyer: value ?? "—", donor: value ?? "—", result: "match" as const }));

  return {
    reg,
    overall: fitmentOverall(rows, partKeyCode(c.part?.categoryName, d)?.field),
    checkedAt: now.toISO() as string,
    rows,
    // Field names above are data (matched against the part's key field), so they stay English.
    buyerVehicleSummary: i18n.t("Sample result", { ns: "messages" }),
    note: i18n.t("Sample result: this preview doesn't look up real cars, so it copies the donor's details.", { ns: "messages" }),
  };
}

const LIST_HIDDEN_KEY = "messagesInbox.listHidden";

function readListHidden() {
  try {
    return window.localStorage.getItem(LIST_HIDDEN_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * The Messages page. With the "Messages Inbox" feature flag it runs on the real API; without it,
 * only on localhost, on sample data (nothing is sent). Anyone else gets No access.
 */
export default function MessagesInbox() {
  const { features } = useCustomerDetails();
  const live = useFeatureEnabled("Messages Inbox");

  // Still finding out which features this Wilma has; if that fails, there's no flag, so no access.
  if (!features.data && !features.isError) {
    return (
      <div className="flex justify-center py-20">
        <LoadingWheel />
      </div>
    );
  }
  if (!live && !isLocalDebug()) return <NoAccess />;

  return (
    <InboxLiveContext.Provider value={live}>
      <InboxWorkspace key={live ? "live" : "sample"} live={live} />
    </InboxLiveContext.Provider>
  );
}

function errorText(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

function InboxWorkspace({ live }: { live: boolean }) {
  const { t } = useTranslation("messages");
  const { customer } = useCustomerDetails();
  // Each customer signs off as themselves (from Betty), so no shop name is written into the code.
  const shopName = customer.data?.data?.name ?? "";
  const { setInfo } = usePageNameContext();
  const auth = useAuth();
  const { conversations: query, refresh } = useMessagesInbox(live);
  const { reply, cancelOrder, refundOrder, refundReturn } = useMessagesInboxMutations();

  const [conversations, setConversations] = useState<IInboxConversation[]>([]);
  const [now, setNow] = useState(() => DateTime.utc());
  const [folder, setFolder] = useState<InboxFolder>("needs_reply");
  const [channel, setChannel] = useState<InboxChannel | "all">("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<InboxSort>("least_time_left");
  const [selectedId, setSelectedId] = useState<string>();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // Photos picked for each conversation's reply. Preview only: they stay in this browser tab.
  const [attachments, setAttachments] = useState<Record<string, IInboxAttachment[]>>({});
  const [noteMode, setNoteMode] = useState(false);
  const [regOverrides, setRegOverrides] = useState<Record<string, string>>({});
  const [panelTab, setPanelTab] = useState<PanelTab>("details");
  const [panelHidden, setPanelHidden] = useState(false);
  // Hiding the conversation list gives the thread and side panel more room. Remembered in this
  // browser only; prev/next in the thread header still move between conversations.
  const [listHidden, setListHidden] = useState(() => readListHidden());
  // Live: replies that really went, until eBay / PartPro show them in the thread.
  const [pendingReplies, setPendingReplies] = useState<Record<string, IInboxMessage[]>>({});
  const [sendNotice, setSendNotice] = useState<IInboxNotice>();
  // Live: what the last cancel or refund came back with, per conversation, so it outlives the panel.
  const [orderNotices, setOrderNotices] = useState<Record<string, IInboxNotice>>({});
  // Live: a cancel or refund is with eBay / PartPro. Stay on this conversation until it answers, or
  // the result would be lost.
  const orderActionBusy = cancelOrder.isLoading || refundOrder.isLoading || refundReturn.isLoading;
  const changeFolder = (f: InboxFolder) => !orderActionBusy && setFolder(f);
  const changeChannel = (ch: InboxChannel | "all") => !orderActionBusy && setChannel(ch);

  function toggleList(hidden: boolean) {
    setListHidden(hidden);
    try {
      window.localStorage.setItem(LIST_HIDDEN_KEY, hidden ? "1" : "0");
    } catch {
      // Private window or storage blocked: it just won't be remembered.
    }
  }

  useEffect(() => {
    setInfo({ name: t("Messages"), desc: live ? t("eBay and PartPro in one inbox") : t("eBay and PartPro in one inbox · preview on sample data, nothing is sent") });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setInfo is a new function each render; set once per language
  }, [t, live]);

  useEffect(() => {
    if (!query.data) return;
    const rows = query.data;
    // Live: every refresh replaces the list (keeping what's been opened). Sample: load once; after
    // that the preview's own edits win.
    if (live) setConversations((previous) => rows.map((c) => mergeRow(c, previous.find((p) => p.id === c.id))));
    else if (conversations.length === 0) setConversations(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the list itself reloads
  }, [query.data]);

  useEffect(() => {
    const timer = setInterval(() => setNow(DateTime.utc()), 60_000);
    return () => clearInterval(timer);
  }, []);

  const users = useMemo<IInboxUser[]>(
    () => [{ id: SAMPLE_ME_ID, forename: auth.forename || t("You"), surname: auth.surname || "", role: t("You") }, ...(live ? [] : sampleInboxUsers)],
    [auth.forename, auth.surname, t, live]
  );
  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const me = users[0];

  const inChannel = useMemo(() => conversations.filter((c) => matchesChannel(c, channel)), [conversations, channel]);

  const counts = useMemo(
    () => Object.fromEntries(ALL_FOLDERS.map((f) => [f, inChannel.filter((c) => inFolder(c, f)).length])) as Record<InboxFolder, number>,
    [inChannel]
  );

  const visible = useMemo(
    () => {
      const list = inChannel.filter((c) => inFolder(c, folder) && matchesSearch(c, search));
      return folder === "sent" ? sortBySent(list) : sortConversations(list, sort);
    },
    [inChannel, folder, search, sort]
  );

  const partProWaiting = useMemo(
    () => sortConversations(conversations.filter((c) => c.channel === "partpro" && inFolder(c, "needs_reply")), "oldest_first"),
    [conversations]
  );

  const selectedRow = conversations.find((c) => c.id === selectedId);
  const { thread } = useMessagesInboxThread(selectedId, live);
  // The order is only shown to the conversation of the buyer who placed it (PartPro rows carry the order number).
  const { partContext } = useMessagesInboxPartContext(selectedRow?.partId, live, {
    buyer: selectedRow?.buyer.username,
    orderNumber: selectedRow?.channel === "partpro" ? selectedRow?.orderNumber : undefined,
  });
  const selected =
    live && selectedRow ? withThreadAndContext(selectedRow, thread.data, partContext.data, pendingReplies[selectedRow.id]) : selectedRow;
  const selectedIndex = visible.findIndex((c) => c.id === selectedId);

  // Forget pending replies once the thread from eBay / PartPro has them.
  useEffect(() => {
    const data = thread.data;
    if (!data) return;
    setPendingReplies((all) => {
      const pending = all[data.id];
      if (!pending?.length) return all;
      const arrived = arrivedPendingIds(messagesFromThread(data), pending);
      return arrived.length ? { ...all, [data.id]: pending.filter((p) => !arrived.includes(p.id)) } : all;
    });
  }, [thread.data]);

  // Keep a conversation open: when the selected one drops out of the list (sent or
  // no reply needed), open the one that took its place, so you can work down the list.
  const lastIndex = useRef(0);
  if (selectedIndex >= 0) lastIndex.current = selectedIndex;
  useEffect(() => {
    lastIndex.current = 0; // a different folder or filter starts from the top
  }, [folder, channel, search]);
  useEffect(() => {
    if (visible.length > 0 && selectedIndex < 0) select(visible[Math.min(lastIndex.current, visible.length - 1)].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the list itself changes
  }, [visible]);

  function select(id: string) {
    if (orderActionBusy) return;
    setSelectedId(id);
    setNoteMode(false);
    const c = conversations.find((x) => x.id === id);
    setPanelTab(c?.returnCase || c?.ebayCase ? "return" : "details");
    update(id, (x) => ({ ...x, unread: false }));
    // "Sent" stays as the next conversation opens; a failure belongs to the one it happened on.
    setSendNotice((notice) => (notice?.tone === "green" ? notice : undefined));
  }

  function update(id: string, change: (c: IInboxConversation) => IInboxConversation) {
    setConversations((list) => list.map((c) => (c.id === id ? change(c) : c)));
  }

  function addMessages(c: IInboxConversation, ...messages: Omit<IInboxMessage, "id" | "sentAt">[]) {
    const at = DateTime.utc().toISO() as string;
    return { ...c, messages: [...c.messages, ...messages.map((m) => ({ ...m, id: nextId(), sentAt: at }))] };
  }

  function systemEvent(body: string): Omit<IInboxMessage, "id" | "sentAt"> {
    return { direction: "system", body };
  }

  function mergeValues(c: IInboxConversation) {
    return {
      // Before a sale we only have the eBay username, so greet them as "Hi there".
      buyer_first_name: buyerFirstName(c.buyer.fullName) ?? "there",
      shop_name: shopName,
      part_title: c.part?.shortTitle ?? c.itemTitle,
      order_no: c.order?.orderNumber,
      courier: c.order?.courier,
      tracking_no: c.order?.trackingNumber,
      est_delivery: c.order?.estimatedDelivery ? buyerFacingDate(c.order.estimatedDelivery, "cccc d LLL") : undefined,
    };
  }

  function setDraft(id: string, text: string) {
    setDrafts((d) => ({ ...d, [id]: text }));
  }

  function changeStatus(c: IInboxConversation, status: IInboxConversation["status"]) {
    if (c.status === status) return c;
    return addMessages({ ...c, status }, systemEvent(t("Status changed to {{status}} · {{name}}", { status: t(STATUS_LABELS[status]), name: me.forename })));
  }

  /**
   * Live: sends the reply through the API. It only joins the thread (and moves to Sent) when eBay or
   * PartPro took it; when sending is switched off on this Wilma nothing is sent and the draft stays.
   */
  function sendLive(c: IInboxConversation, body: string) {
    const channel = c.channel === "ebay" ? "eBay" : "PartPro";
    setSendNotice(undefined);
    reply.mutate(
      { conversationId: c.id, body },
      {
        onSuccess: (result) => {
          if (result.blocked) {
            setSendNotice({ tone: "amber", text: t("Sending is switched off here – nothing was sent") });
            return;
          }
          const at = DateTime.utc().toISO() as string;
          setPendingReplies((all) => ({ ...all, [c.id]: [...(all[c.id] ?? []), { id: nextId(), direction: "us", body, sentAt: at, pending: true }] }));
          update(c.id, (x) => ({ ...x, status: "waiting_on_buyer", lastSentAt: at, unread: false }));
          setDraft(c.id, "");
          setSendNotice({ tone: "green", text: t("Reply sent to {{name}} on {{channel}}.", { name: c.buyer.username, channel }) });
        },
        onError: (error) => setSendNotice({ tone: "red", text: errorText(error, t("Not sent – {{channel}} didn't accept it. Try again.", { channel })) }),
      }
    );
  }

  /** Live: cancel or refund for real, once confirmed in the panel. Says what happened. */
  async function orderActionLive(c: IInboxConversation, action: IOrderAction): Promise<IInboxNotice> {
    setOrderNotices(({ [c.id]: _, ...rest }) => rest);
    const notice = await orderActionResult(c, action);
    setOrderNotices((all) => ({ ...all, [c.id]: notice }));
    return notice;
  }

  async function orderActionResult(c: IInboxConversation, action: IOrderAction): Promise<IInboxNotice> {
    const partId = c.partId ?? c.part?.id;
    if (!partId || !c.order) return { tone: "red", text: t("There's no order on this conversation to do that to.") };
    const ids = { partId, conversationId: c.id };
    // Whole pence only: the input won't confirm a third decimal place, and eBay wouldn't take one.
    const amount = Math.round((action.amount ?? 0) * 100) / 100;
    const full = action.kind === "refund_full";
    try {
      const result =
        action.kind === "cancel"
          ? await cancelOrder.mutateAsync({ ...ids, reason: action.reasonCode as InboxCancelReason })
          : c.returnCase
            ? await refundReturn.mutateAsync({ ...ids, returnId: c.returnCase.returnId, full, amount, comment: null })
            : await refundOrder.mutateAsync({ ...ids, full, amount, reason: action.reasonCode ?? "", comment: null });
      if (result.blocked) return { tone: "amber", text: t("Sending is switched off here – nothing was cancelled or refunded.") };
      // The message to the buyer goes in the reply box to check and send, not straight out.
      if (action.messageToBuyer?.trim()) {
        setNoteMode(false);
        setDraft(c.id, fillMergeFields(action.messageToBuyer, mergeValues(c)));
      }
      if (action.kind === "cancel") return { tone: "green", text: t("Order {{number}} cancelled.", { number: c.order.orderNumber }) };
      return {
        tone: "green",
        text: result.refundStatus
          ? t("Refund of {{amount}} sent ({{status}}).", { amount: PriceFormat(amount), status: result.refundStatus })
          : t("Refund of {{amount}} sent.", { amount: PriceFormat(amount) }),
      };
    } catch (error) {
      return { tone: "red", text: errorText(error, t("It didn't go through – nothing was cancelled or refunded.")) };
    }
  }

  /** Sending moves the conversation to Sent (waiting on the buyer) until they write back. */
  function send() {
    if (!selected) return;
    const body = (drafts[selected.id] ?? "").trim();
    const photos = noteMode ? [] : attachments[selected.id] ?? [];
    if (!body && photos.length === 0) return;
    if (live) {
      if (!noteMode && !reply.isLoading) sendLive(selected, body);
      return;
    }

    if (noteMode) {
      update(selected.id, (c) => addMessages(c, { direction: "note", body, authorId: SAMPLE_ME_ID }));
    } else {
      update(selected.id, (c) =>
        changeStatus(addMessages(c, { direction: "us", body, authorId: SAMPLE_ME_ID, previewOnly: true, attachments: photos }), "waiting_on_buyer")
      );
      setAttachments((a) => ({ ...a, [selected.id]: [] }));
    }
    setDraft(selected.id, "");
  }


  const frame = "flex h-[calc(100vh-11rem)] min-h-[34rem] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm";

  if (live && !query.data) {
    return (
      <div className={classNames(frame, "items-center justify-center")}>
        {query.isError ? (
          <InboxError text={errorText(query.error, t("Couldn't load the messages."))} onRetry={() => query.refetch()} />
        ) : (
          <InboxLoading text={t("Fetching messages from eBay and PartPro – this can take up to 30 seconds…")} />
        )}
      </div>
    );
  }

  if (!live && (query.isLoading || conversations.length === 0)) {
    return (
      <div className="flex justify-center py-20">
        <LoadingWheel />
      </div>
    );
  }

  const reg = selected ? regOverrides[selected.id] ?? selected.fitment?.reg ?? regFromThread(selected) : null;
  const replyBy = selected ? replyByLabel(selected.replyBy, selected.lastBuyerMessageAt, now) : undefined;
  const replyByTime = selected ? formatDateTime(selected.replyBy, INBOX_TIME) : "";

  return (
    <div className={frame}>
      <InboxTopBar
        folder={folder}
        onFolder={changeFolder}
        counts={counts}
        channel={channel}
        onChannel={changeChannel}
        partProWaiting={partProWaiting.length > 0}
        search={search}
        onSearch={setSearch}
        onRefresh={live ? refresh : undefined}
        refreshing={query.isFetching}
        disabled={orderActionBusy}
      />
      {live && query.isError && <InboxError text={errorText(query.error, t("Couldn't refresh the messages – showing the last ones loaded."))} onRetry={() => query.refetch()} />}

      <div className="flex min-h-0 flex-1">
        {listHidden ? (
          <button
            type="button"
            onClick={() => toggleList(false)}
            title={t("Show the list")}
            className="flex w-10 shrink-0 flex-col items-center gap-2 border-r border-gray-200 bg-white pt-3 text-gray-500 hover:bg-gray-50"
          >
            <ChevronDoubleRightIcon className="h-4 w-4" />
            {counts.needs_reply > 0 && <span className="rounded-full bg-amber-100 px-1.5 text-[11px] font-semibold text-amber-800">{counts.needs_reply}</span>}
            <span className="text-[12px] font-medium [writing-mode:vertical-rl]">{t("Conversations")}</span>
          </button>
        ) : (
          <ConversationList
            onHide={() => toggleList(true)}
            conversations={visible}
            folder={folder}
            selectedId={selectedId}
            onSelect={select}
            sort={sort}
            onSort={setSort}
            now={now}
            nudge={
              channel === "ebay" && partProWaiting.length > 0
                ? { count: partProWaiting.length, oldest: partProWaiting[0], onShow: () => changeChannel("partpro") }
                : undefined
            }
            waitingOnBuyerCount={counts.sent}
            onFolder={changeFolder}
            disabled={orderActionBusy}
          />
        )}

        {selected && visible.some((c) => c.id === selected.id) ? (
          <>
            <ThreadView
              conversation={selected}
              usersById={usersById}
              users={users}
              position={{ index: selectedIndex, total: visible.length }}
              onPrev={() => selectedIndex > 0 && select(visible[selectedIndex - 1].id)}
              onNext={() => selectedIndex < visible.length - 1 && select(visible[selectedIndex + 1].id)}
              onNeedsReply={() => update(selected.id, (c) => changeStatus(c, "needs_reply"))}
              onReg={(r) => {
                setRegOverrides((o) => ({ ...o, [selected.id]: r }));
                setPanelTab("details");
                setPanelHidden(false);
              }}
              draft={drafts[selected.id] ?? ""}
              attachments={attachments[selected.id] ?? []}
              onAttach={(files) =>
                setAttachments((a) => ({
                  ...a,
                  [selected.id]: [...(a[selected.id] ?? []), ...files.map((f) => ({ name: f.name, url: URL.createObjectURL(f) }))],
                }))
              }
              onRemoveAttachment={(index) => setAttachments((a) => ({ ...a, [selected.id]: (a[selected.id] ?? []).filter((_, i) => i !== index) }))}
              onDraft={(text) => setDraft(selected.id, text)}
              noteMode={noteMode}
              onNoteMode={setNoteMode}
              templates={live ? [] : sampleInboxTemplates}
              onTemplate={(t) => {
                setNoteMode(false);
                setDraft(selected.id, fillMergeFields(t.body, mergeValues(selected)));
              }}
              onSend={send}
              onNoReplyNeeded={() => update(selected.id, (c) => changeStatus(c, "no_reply_needed"))}
              replyByText={replyBy ? `${replyByTime} (${replyBy.text})` : ""}
              threadStatus={
                live
                  ? { loading: thread.isFetching && !thread.data, error: thread.isError ? errorText(thread.error, t("Couldn't load this conversation.")) : undefined, onRetry: () => thread.refetch() }
                  : undefined
              }
              sending={reply.isLoading}
              busy={orderActionBusy}
              sendNotice={sendNotice}
            />

            {panelHidden ? (
              <button type="button" onClick={() => setPanelHidden(false)} title={t("Show panel")} className="flex w-10 shrink-0 justify-center border-l border-gray-200 bg-white pt-3 text-gray-500 hover:bg-gray-50">
                <ViewColumnsIcon className="h-4 w-4" />
              </button>
            ) : (
              <ContextPanel
                c={selected}
                tab={panelTab}
                onTab={setPanelTab}
                onHide={() => setPanelHidden(true)}
                usersById={usersById}
                contextStatus={
                  live && selected.partId
                    ? {
                        loading: partContext.isFetching && !partContext.data,
                        error: partContext.isError ? errorText(partContext.error, t("Couldn't load the part and order.")) : undefined,
                        onRetry: () => partContext.refetch(),
                      }
                    : undefined
                }
                orderNotice={orderNotices[selected.id]}
                onClearOrderNotice={() => setOrderNotices(({ [selected.id]: _, ...rest }) => rest)}
                reg={reg}
                onReg={(r) => setRegOverrides((o) => ({ ...o, [selected.id]: r }))}
                onCompare={(r) =>
                  update(selected.id, (c) =>
                    addMessages(
                      { ...c, fitment: c.fitment?.reg === r ? { ...c.fitment, checkedAt: DateTime.utc().toISO() as string } : sampleCompare(c, r, DateTime.utc()) },
                      systemEvent(t("Fitment compared with donor {{reg}} · {{name}} (sample)", { reg: c.donor ? displayReg(c.donor.vrm) : "", name: me.forename }))
                    )
                  )
                }
                onSendAnswer={(text) => {
                  setNoteMode(false);
                  setDraft(selected.id, fillMergeFields(text, mergeValues(selected)));
                }}
                onAddToReply={(text) => setDraft(selected.id, `${drafts[selected.id] ?? ""}${text}`)}
                fillMerge={(text) => fillMergeFields(text, mergeValues(selected))}
                onWarehouseAlert={(text) =>
                  update(selected.id, (c) => addMessages(c, systemEvent(t("Urgent alert to #warehouse: \"{{text}}\" · {{name}} (preview – not sent)", { text, name: me.forename }))))
                }
                onOrderAction={(action) =>
                  live ? orderActionLive(selected, action) : update(selected.id, (c) =>
                    addMessages(
                      c,
                      systemEvent(
                        t("{{action}} · {{name}} (preview – nothing done)", {
                          action: `${action.kind === "cancel" ? t("Order cancelled") : action.kind === "refund_full" ? t("Refunded in full {{amount}}", { amount: PriceFormat(action.amount ?? 0) }) : t("Partial refund {{amount}}", { amount: PriceFormat(action.amount ?? 0) })}${action.reason ? ` · ${action.reason}` : ""}`,
                          name: me.forename,
                        })
                      ),
                      ...(action.messageToBuyer
                        ? [{ direction: "us" as const, body: fillMergeFields(action.messageToBuyer, mergeValues(c)), authorId: SAMPLE_ME_ID, previewOnly: true }]
                        : [])
                    )
                  )
                }
              />
            )}
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center bg-gray-50 text-[13px] text-gray-500">{t("Pick a conversation from the list.")}</div>
        )}
      </div>
    </div>
  );
}
